import { type Static, Type } from "typebox";

import { defineRoute } from "./route.js";
import {
  DateOnly,
  errors,
  Exact,
  Identifier,
  Nullable,
  StringEnum,
  Timestamp,
} from "./schema.js";

// --- Vehicle documents -------------------------------------------------------

export const vehicleDocumentCategories = [
  "registration",
  "insurance",
  "inspection",
  "other",
] as const;
export const VehicleDocumentCategory = StringEnum(vehicleDocumentCategories);
export type VehicleDocumentCategory = Static<typeof VehicleDocumentCategory>;

export const VehicleDocumentState = StringEnum([
  "current",
  "superseded",
  "archived",
]);
export type VehicleDocumentState = Static<typeof VehicleDocumentState>;

/** Computed by `documentStatus` in @fleetminder/fleet, in the fleet's zone. */
export const DocumentStatus = StringEnum([
  "expired",
  "due_30",
  "due_60",
  "due_90",
  "current",
  "no_expiration",
  "missing",
  "superseded",
  "archived",
]);
export type DocumentStatus = Static<typeof DocumentStatus>;

export const VehicleDocumentFile = Exact({
  addedAt: Timestamp,
  id: Identifier,
  mimeType: Type.String(),
  originalFilename: Type.String(),
  sizeBytes: Type.Integer(),
});
export type VehicleDocumentFile = Static<typeof VehicleDocumentFile>;

export const VehicleDocument = Exact({
  archivedAt: Nullable(Timestamp),
  category: VehicleDocumentCategory,
  createdAt: Timestamp,
  createdByName: Nullable(Type.String()),
  /** Whole days to expiry in the fleet's zone; null when it does not apply. */
  daysRemaining: Nullable(Type.Integer()),
  driverVisible: Type.Boolean(),
  effectiveOn: Nullable(DateOnly),
  expiresOn: Nullable(DateOnly),
  /** The file on file now; null when none was attached. */
  file: Nullable(VehicleDocumentFile),
  id: Identifier,
  issuer: Nullable(Type.String()),
  jurisdiction: Nullable(Type.String()),
  note: Nullable(Type.String()),
  referenceSuffix: Nullable(Type.String()),
  reminderLeadDays: Type.Array(Type.Integer()),
  responsibleName: Nullable(Type.String()),
  responsibleUserId: Nullable(Identifier),
  state: VehicleDocumentState,
  status: DocumentStatus,
  supersededAt: Nullable(Timestamp),
  supersedesDocumentId: Nullable(Identifier),
  title: Type.String(),
  updatedAt: Timestamp,
  vehicleId: Identifier,
  verifiedAt: Nullable(Timestamp),
  verifiedByName: Nullable(Type.String()),
  version: Type.Integer(),
});
export type VehicleDocument = Static<typeof VehicleDocument>;

export const VehicleDocumentList = Exact({
  items: Type.Array(VehicleDocument),
  /** Members who can be named responsible for a document. */
  responsibleChoices: Type.Array(
    Exact({ name: Type.String(), userId: Identifier }),
  ),
  /** The organization's zone and today's date in it, for previews. */
  timeZone: Type.String(),
  today: DateOnly,
});
export type VehicleDocumentList = Static<typeof VehicleDocumentList>;

const DocumentFields = {
  driverVisible: Type.Boolean(),
  effectiveOn: Nullable(DateOnly),
  expiresOn: Nullable(DateOnly),
  issuer: Nullable(Type.String({ maxLength: 160 })),
  jurisdiction: Nullable(Type.String({ maxLength: 80 })),
  note: Nullable(Type.String({ maxLength: 2_000 })),
  /** Only the last few characters: the full number is never stored. */
  referenceSuffix: Nullable(Type.String({ pattern: "^[A-Za-z0-9]{1,6}$" })),
  reminderLeadDays: Type.Array(Type.Integer({ maximum: 365, minimum: 1 }), {
    maxItems: 5,
    uniqueItems: true,
  }),
  responsibleUserId: Nullable(Identifier),
  title: Type.String({ maxLength: 160, minLength: 1 }),
};

/** Everything but the category is optional when a document is added. */
const NewDocumentFields = {
  driverVisible: Type.Optional(DocumentFields.driverVisible),
  effectiveOn: Type.Optional(DocumentFields.effectiveOn),
  expiresOn: Type.Optional(DocumentFields.expiresOn),
  fileId: Type.Optional(Identifier),
  issuer: Type.Optional(DocumentFields.issuer),
  jurisdiction: Type.Optional(DocumentFields.jurisdiction),
  note: Type.Optional(DocumentFields.note),
  referenceSuffix: Type.Optional(DocumentFields.referenceSuffix),
  reminderLeadDays: Type.Optional(DocumentFields.reminderLeadDays),
  responsibleUserId: Type.Optional(DocumentFields.responsibleUserId),
  /** Defaults to the category's name; required for "other". */
  title: Type.Optional(DocumentFields.title),
};

const documentPath =
  "/organizations/:organizationId/vehicles/:vehicleId/documents/:documentId";

// A missing vehicle or document, or one in another organization, is a 404.
// 409 is a stale version, a document no longer current, or a second current
// document of a kind that allows one.

export const listVehicleDocuments = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/vehicles/:vehicleId/documents",
  response: { 200: VehicleDocumentList, ...errors(401, 403, 404) },
  tags: ["documents"],
});

export const createVehicleDocument = defineRoute({
  body: Exact({ category: VehicleDocumentCategory, ...NewDocumentFields }),
  method: "POST",
  path: "/organizations/:organizationId/vehicles/:vehicleId/documents",
  response: { 201: VehicleDocument, ...errors(401, 403, 404, 409, 422) },
  tags: ["documents"],
});

/** Corrects what was entered. Changing the dates clears the check on them. */
export const updateVehicleDocument = defineRoute({
  body: Exact(
    {
      category: Type.Optional(VehicleDocumentCategory),
      driverVisible: Type.Optional(DocumentFields.driverVisible),
      effectiveOn: Type.Optional(DocumentFields.effectiveOn),
      expiresOn: Type.Optional(DocumentFields.expiresOn),
      issuer: Type.Optional(DocumentFields.issuer),
      jurisdiction: Type.Optional(DocumentFields.jurisdiction),
      note: Type.Optional(DocumentFields.note),
      referenceSuffix: Type.Optional(DocumentFields.referenceSuffix),
      reminderLeadDays: Type.Optional(DocumentFields.reminderLeadDays),
      responsibleUserId: Type.Optional(DocumentFields.responsibleUserId),
      title: Type.Optional(DocumentFields.title),
      version: Type.Integer({ minimum: 1 }),
    },
    { minProperties: 2 },
  ),
  method: "POST",
  path: `${documentPath}/details`,
  response: { 200: VehicleDocument, ...errors(401, 403, 404, 409, 422) },
  tags: ["documents"],
});

/** Swaps a wrong upload. The earlier file is kept, no longer shown. */
export const replaceVehicleDocumentFile = defineRoute({
  body: Exact({ fileId: Identifier, version: Type.Integer({ minimum: 1 }) }),
  method: "POST",
  path: `${documentPath}/file`,
  response: { 200: VehicleDocument, ...errors(401, 403, 404, 409, 422) },
  tags: ["documents"],
});

/**
 * A new card or certificate replacing this one. The answer is the new
 * current document; this one is kept, superseded, with its file.
 */
export const renewVehicleDocument = defineRoute({
  body: Exact({
    ...NewDocumentFields,
    version: Type.Integer({ minimum: 1 }),
  }),
  method: "POST",
  path: `${documentPath}/renewals`,
  response: { 201: VehicleDocument, ...errors(401, 403, 404, 409, 422) },
  tags: ["documents"],
});

/** Someone compared the dates entered with the document itself. */
export const verifyVehicleDocument = defineRoute({
  body: Exact({ version: Type.Integer({ minimum: 1 }) }),
  method: "POST",
  path: `${documentPath}/verification`,
  response: { 200: VehicleDocument, ...errors(401, 403, 404, 409) },
  tags: ["documents"],
});

export const archiveVehicleDocument = defineRoute({
  body: Exact({ version: Type.Integer({ minimum: 1 }) }),
  method: "POST",
  path: `${documentPath}/archive`,
  response: { 200: VehicleDocument, ...errors(401, 403, 404, 409) },
  tags: ["documents"],
});

export const VehicleDocumentDownload = Exact({
  /** When the link stops working; ask again after that. */
  expiresAt: Timestamp,
  url: Type.String(),
});
export type VehicleDocumentDownload = Static<typeof VehicleDocumentDownload>;

/**
 * A short-lived link to the document's file, issued after the permission
 * check and recorded in the activity log. 404 when it has no file, 409 when
 * this deployment has no file storage.
 */
export const downloadVehicleDocument = defineRoute({
  method: "POST",
  path: `${documentPath}/downloads`,
  response: {
    200: VehicleDocumentDownload,
    ...errors(401, 403, 404, 409),
  },
  tags: ["documents"],
});
